/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import { Button, Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import CountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../core/i18n";
import useLanguageStore from "../stores/languageStore";
import { getLocalizedPath } from "../utils/urlUtils";

// Map language codes to country codes for flags (only for languages that have flags)
const LANGUAGE_TO_COUNTRY: Partial<Record<SupportedLanguage, string>> = {
  en: "US",
  es: "ES",
  fr: "FR",
  de: "DE",
  it: "IT",
  pt: "PT",
  ru: "RU",
  ja: "JP",
  pl: "PL",
  hi: "IN",
  tr: "TR",
  nl: "NL",
  vi: "VN",
  ko: "KR",
};

export function LanguageSwitcher(): JSX.Element {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage, setLanguage, getCurrentLanguageInfo } =
    useLanguageStore();

  const handleLanguageChange = (language: SupportedLanguage) => {
    // Update store first - this is synchronous with Zustand
    // The store update happens immediately, so LanguageRedirect will read the updated value
    setLanguage(language);
    i18n.changeLanguage(language);

    // Get the current pathname without any language prefix
    const pathWithoutLang = location.pathname.replace(
      /^\/[a-z]{2}(?=\/|$)/,
      "",
    );

    // Only add language prefix for routes that support it
    const supportedLangRoutes = ["", "/", "/home", "/discover"];
    const isTagRoute = /^\/tag\/[^/]+$/.test(pathWithoutLang);
    const isCharacterInfoRoute = /^\/character-info\/[^/]+$/.test(
      pathWithoutLang,
    );
    const shouldAddLangPrefix =
      supportedLangRoutes.includes(pathWithoutLang) ||
      isTagRoute ||
      isCharacterInfoRoute;

    if (shouldAddLangPrefix) {
      // For supported routes, add language prefix using the utility function
      // Use the language parameter directly, not from store, to avoid race conditions
      const newPath =
        getLocalizedPath(
          pathWithoutLang === "/" ? "/" : pathWithoutLang,
          language,
        ) + location.search;
      navigate(newPath, { replace: true });
    } else {
      // For internal routes like /account, just stay on the same path
      // No navigation needed, just language change
    }
  };

  const currentLanguageInfo = getCurrentLanguageInfo();
  const currentCountryCode =
    LANGUAGE_TO_COUNTRY[currentLanguage as SupportedLanguage];

  return (
    <Dropdown>
      <MenuButton
        component={Button}
        variant="soft"
        color="neutral"
        size="sm"
        startDecorator={
          currentCountryCode ? (
            <CountryFlag
              countryCode={currentCountryCode}
              svg
              aria-label={`${currentLanguageInfo.name} flag`}
              title={`${currentLanguageInfo.name} flag`}
              style={{
                width: "16px",
                height: "12px",
              }}
            />
          ) : null
        }
        sx={{
          minWidth: 120,
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          bgcolor: "background.level1",
          opacity: 0.75,
          "&:hover": {
            bgcolor: "background.level2",
          },
          transition: "all 0.2s ease",
        }}
      >
        {currentLanguageInfo.nativeName}
      </MenuButton>
      <Menu
        placement="bottom-end"
        sx={{
          minWidth: 200,
          maxHeight: "calc(100vh - 120px)",
          overflow: "auto",
          zIndex: 1000,
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
          // Prevent scroll chaining on mobile
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
          },
        }}
      >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => {
          const countryCode = LANGUAGE_TO_COUNTRY[code as SupportedLanguage];
          return (
            <MenuItem
              key={code}
              onClick={() => handleLanguageChange(code as SupportedLanguage)}
              selected={currentLanguage === code}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {countryCode ? (
                <CountryFlag
                  countryCode={countryCode}
                  svg
                  aria-label={`${info.name} flag`}
                  title={`${info.name} flag`}
                  style={{
                    width: "16px",
                    height: "12px",
                  }}
                />
              ) : null}
              {info.nativeName} ({info.name})
            </MenuItem>
          );
        })}
      </Menu>
    </Dropdown>
  );
}
